import { useMemo, useState } from 'react'
import {
  Box, Breadcrumbs, Typography, Stack, Button, Chip, IconButton, Menu, MenuItem, ListItemIcon, ListItemText,
  Snackbar, Alert, Dialog, DialogTitle, DialogContent, DialogActions, TextField, CircularProgress,
} from '@mui/material'
import { Add, MoreVert, PlayArrow, Check } from '@mui/icons-material'
import { DataGrid, type GridColDef } from '@mui/x-data-grid'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { listWorkOrders, startWorkOrder, resolveWorkOrder, type WorkOrderListItem, type WorkOrderStatus, type WorkOrderPriority } from '../../api/mentenanta'
import { errorMessage } from '../../api/client'
import WorkOrderFormDialog from './WorkOrderFormDialog'

const STATUS_LABEL: Record<WorkOrderStatus, string> = { Deschis: 'Deschis', InLucru: 'În lucru', Rezolvat: 'Rezolvat' }
const STATUS_COLOR: Record<WorkOrderStatus, 'warning' | 'info' | 'success'> = { Deschis: 'warning', InLucru: 'info', Rezolvat: 'success' }
const PRIORITY_LABEL: Record<WorkOrderPriority, string> = { Scazuta: 'Scăzută', Normala: 'Normală', Ridicata: 'Ridicată', Urgenta: 'Urgentă' }

export default function WorkOrdersPage() {
  const [formOpen, setFormOpen] = useState(false)
  const [resolveTarget, setResolveTarget] = useState<WorkOrderListItem | null>(null)
  const [menuAnchor, setMenuAnchor] = useState<{ el: HTMLElement; row: WorkOrderListItem } | null>(null)
  const [error, setError] = useState<string | null>(null)
  const qc = useQueryClient()
  const { data, isLoading } = useQuery({ queryKey: ['mentenanta', 'ordine-lucru'], queryFn: () => listWorkOrders() })
  const closeMenu = () => setMenuAnchor(null)
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['mentenanta', 'ordine-lucru'] })
    void qc.invalidateQueries({ queryKey: ['mentenanta', 'echipamente'] })
  }

  const startMutation = useMutation({ mutationFn: startWorkOrder, onSuccess: invalidate, onError: (e) => setError(errorMessage(e)) })

  const columns = useMemo<GridColDef<WorkOrderListItem>[]>(() => [
    { field: 'number', headerName: 'Nr.', width: 130, renderCell: (p) => <code>{p.row.number}</code> },
    { field: 'equipmentName', headerName: 'Echipament', flex: 1, minWidth: 200, renderCell: (p) => `${p.row.equipmentCode} — ${p.row.equipmentName}` },
    { field: 'type', headerName: 'Tip', width: 110 },
    { field: 'priority', headerName: 'Prioritate', width: 110, valueGetter: (v: WorkOrderPriority) => PRIORITY_LABEL[v] },
    { field: 'reportedAt', headerName: 'Raportat', width: 160, valueFormatter: (v: string) => new Date(v).toLocaleString('ro-RO') },
    { field: 'resolvedAt', headerName: 'Rezolvat', width: 160, valueFormatter: (v: string | null) => v ? new Date(v).toLocaleString('ro-RO') : '—' },
    { field: 'status', headerName: 'Status', width: 130, sortable: false, renderCell: (p) => <Chip size="small" label={STATUS_LABEL[p.row.status]} color={STATUS_COLOR[p.row.status]} variant="outlined" /> },
    {
      field: 'actions', headerName: '', width: 56, sortable: false, filterable: false, disableColumnMenu: true,
      renderCell: (p) => (
        <IconButton size="small" onClick={(e) => setMenuAnchor({ el: e.currentTarget, row: p.row })}>
          <MoreVert fontSize="small" />
        </IconButton>
      ),
    },
  ], [])

  return (
    <Stack spacing={2}>
      <Breadcrumbs separator="/" sx={{ fontSize: 12.5 }}>
        <Typography variant="inherit" color="text.secondary">Mentenanță</Typography>
        <Typography variant="inherit" color="text.primary">Ordine de lucru</Typography>
      </Breadcrumbs>
      <Typography variant="h2">Ordine de lucru</Typography>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="contained" startIcon={<Add />} onClick={() => setFormOpen(true)}>Ordin nou</Button>
      </Box>
      <Box sx={{ height: 560, bgcolor: 'background.paper', borderRadius: 1.5 }}>
        <DataGrid rows={data ?? []} columns={columns} loading={isLoading} density="comfortable" disableRowSelectionOnClick
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }} pageSizeOptions={[25, 50]} />
      </Box>

      <WorkOrderFormDialog open={formOpen} onClose={() => setFormOpen(false)} />
      <ResolveDialog target={resolveTarget} onClose={() => setResolveTarget(null)} onResolved={invalidate} />

      <Menu anchorEl={menuAnchor?.el} open={!!menuAnchor} onClose={closeMenu}>
        {menuAnchor && [
          <MenuItem key="start" disabled={menuAnchor.row.status !== 'Deschis'}
            onClick={() => { startMutation.mutate(menuAnchor.row.id); closeMenu() }}>
            <ListItemIcon><PlayArrow fontSize="small" /></ListItemIcon><ListItemText>Începe lucrul</ListItemText>
          </MenuItem>,
          <MenuItem key="resolve" disabled={menuAnchor.row.status === 'Rezolvat'}
            onClick={() => { setResolveTarget(menuAnchor.row); closeMenu() }}>
            <ListItemIcon><Check fontSize="small" /></ListItemIcon><ListItemText>Rezolvă</ListItemText>
          </MenuItem>,
        ]}
      </Menu>

      <Snackbar open={!!error} autoHideDuration={5000} onClose={() => setError(null)}>
        <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>
      </Snackbar>
    </Stack>
  )
}

function ResolveDialog({ target, onClose, onResolved }: { target: WorkOrderListItem | null; onClose: () => void; onResolved: () => void }) {
  const open = target !== null
  const [notes, setNotes] = useState('')
  const mutation = useMutation({
    mutationFn: () => resolveWorkOrder(target!.id, notes || null),
    onSuccess: () => { setNotes(''); onResolved(); onClose() },
  })
  const handleClose = () => { mutation.reset(); setNotes(''); onClose() }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Rezolvă ordinul {target?.number}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          <TextField label="Notă de rezolvare (opțional)" value={notes} onChange={(e) => setNotes(e.target.value)} fullWidth multiline minRows={3} />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={handleClose}>Anulează</Button>
        <Button variant="contained" onClick={() => mutation.mutate()} disabled={mutation.isPending}
          startIcon={mutation.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}>
          Rezolvă
        </Button>
      </DialogActions>
    </Dialog>
  )
}
